import { Player } from "@remotion/player";
import { MainComposition } from "@/remotion/Composition";
import logo from "../../logo.png";
import { ModeTabs } from "./ModeTabs";
import { Timeline } from "./timeline";
import { useTimeline } from "./timeline/useTimeline";
import { useAppStore } from "@/lib/store";
import { useEditorLogic } from "./useEditorLogic";
import { ClassicModeUI } from "./ClassicModeUI";
import { WaveformControls } from "./timeline/WaveformControls";

export function Editor() {
  const { config, setConfig } = useAppStore();
  const t = useTimeline(config, setConfig);

  const {
    wordCount,
    lineCount,
    w,
    h,
    fps,
    durationInFrames,
    handleReset,
    handleAudioUpload,
    handlePlayerRef,
  } = useEditorLogic({ config, setConfig, timeline: t });

  const isSyncMode = config.renderMode === 'sync';

  return (
    <div className="flex h-dvh flex-col overflow-hidden">
      <div className="editor-scroll flex-1 flex flex-col gap-5 overflow-y-auto px-5 pb-5 sm:px-7 lg:px-8">
        <header className="studio-header">
          <div className="flex flex-col gap-6 w-full">
            <div className="flex items-center justify-between">
              <div className="flex min-w-0 items-center gap-3">
                <div className="brand-mark overflow-hidden">
                  <img src={logo} className="size-full object-cover" alt="Poetic Studio" />
                </div>
                <h1 className="flex items-baseline gap-1.5 truncate font-semibold text-foreground">
                  <span className="text-3xl text-primary sm:text-4xl" style={{ fontFamily: "'Dancing Script', cursive" }}>Poetic</span>
                  <span className="text-lg sm:text-xl font-medium opacity-80 tracking-tight">Studio</span>
                </h1>
              </div>
              <div className="flex items-center gap-4">
                <div className="header-meter hidden sm:flex">
                  <span>{wordCount} words</span>
                  <span>{lineCount} lines</span>
                </div>
                <button
                  onClick={handleReset}
                  className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground hover:text-destructive transition-colors"
                >
                  Reset
                </button>
              </div>
            </div>

            <ModeTabs onChange={setConfig} config={config} />
          </div>
        </header>

        {isSyncMode ? (
          <>
            <div className="hidden">
              <Player
                ref={handlePlayerRef}
                component={MainComposition}
                inputProps={config}
                durationInFrames={durationInFrames}
                fps={fps}
                compositionWidth={w}
                compositionHeight={h}
              />
            </div>
            <Timeline config={config} onChange={setConfig} timeline={t} />
          </>
        ) : (
          <ClassicModeUI 
            config={config} 
            onChange={setConfig} 
            handleAudioUpload={handleAudioUpload} 
          />
        )}
      </div>

      {isSyncMode && (
        <div className="bg-background/80 backdrop-blur-2xl border-t border-white/5 p-4 z-50">
          <div className="max-w-[1200px] mx-auto">
            <WaveformControls
              config={config}
              isPlaying={t.isPlaying}
              currentTime={t.currentTime}
              duration={t.duration}
              musicContainerRef={t.musicContainerRef}
              voContainerRef={t.voContainerRef}
              onTogglePlay={t.togglePlay}
              onSeek={t.seekTo}
              onUpdateConfig={setConfig}
            />
          </div>
        </div>
      )}
    </div>
  );
}
